import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ValidationState } from '@import/enums/validation-state.enum';
import { ProcessedRow } from '@import/interfaces/processed-row.interface';
import { RowAndColumnService } from '@import/services/row-and-column/row-and-column.service';
import { Column } from '../../classes';
import { EmployeeHumiColumnService } from '../column-type/employee-humi-column.service';

const DUPLICATE_EMAIL = 'Duplicate email found in uploaded data';

@Injectable()
export class EmployeeRowAndColumnService extends RowAndColumnService {
    protected readonly type: string = 'employeeRecord';
    protected importUrl = '/v2/import/employees';
    protected validateUrl = '/v2/import/employees/validate';
    protected knownColumnHelperValue = 'Please expand row 2';
    protected knownRowHelperValues = [
        "Please enter this employee's manager's full name or email address.",
        'Required for payroll',
        'Please use the following date format: yyyy-mm-dd',
        'Additional columns can be added and imported as custom fields',
    ];
    constructor(
        protected http: HttpClient,
        protected formBuilder: FormBuilder,
        protected employeeHumiColumnService: EmployeeHumiColumnService
    ) {
        super(http, formBuilder, employeeHumiColumnService);
    }

    validateRow(row: ProcessedRow, emails: string[] = []): Promise<void> {
        return super.validateRow(row, { import: { emails: emails } }).then(() => {
            row.states = this.getEmailFlag(row);
            this.setEmailError(row);
            this.checkUniqueEmail(row);
            this.clearStaleDuplicateErrors();
        });
    }

    clearStaleDuplicateErrors(): void {
        const emailIndex = this.primaryColumn.csvColumn.index;

        const duplicates = this.processedRows.filter((row) => row.errors && row.errors[emailIndex] === DUPLICATE_EMAIL);
        const duplicateEmails = duplicates.reduce((uniqueEmails, duplicate) => {
            uniqueEmails.add(duplicate.raw[emailIndex]);

            return uniqueEmails;
        }, new Set<string>());

        duplicateEmails.forEach((email) => {
            const duplicatesForEmail = duplicates.filter((duplicate) => duplicate.raw[emailIndex] === email);

            if (duplicatesForEmail.length < 2) {
                delete duplicatesForEmail[0].errors[emailIndex];
                this.setEmailError(duplicatesForEmail[0]);
            }
        });
    }

    getUnmatchedCsvColumns(): Column[] {
        return this.columns.filter((column: Column) => column.type === null);
    }

    protected sortFunction(a: ProcessedRow, b: ProcessedRow): number {
        const emailIndex = this.primaryColumn ? this.primaryColumn.csvColumn.index : 0;
        const aVal = a.raw[emailIndex] || '';
        const bVal = b.raw[emailIndex] || '';

        return aVal.localeCompare(bVal);
    }

    protected getEmailFlag(row: ProcessedRow): number {
        return parseInt(Object.values(row.validationResponse.data.attributes).map(Number).join(''), 2);
    }

    protected setEmailError(row: ProcessedRow): void {
        const emailIndex = this.primaryColumn.csvColumn.index;
        if (!row.errors[emailIndex] && row.states === ValidationState.duplicate) {
            row.errors[emailIndex] = 'This email is unavailable';
        }
    }

    private checkUniqueEmail(row: ProcessedRow): void {
        const emailIndex = this.primaryColumn.csvColumn.index;
        const duplicateEmailRows = this.processedRows.filter(
            (r) => r !== row && r.raw[emailIndex] === row.raw[emailIndex]
        );

        if (duplicateEmailRows.length) {
            [row, ...duplicateEmailRows].forEach((r) => (r.errors[emailIndex] = DUPLICATE_EMAIL));
        }
    }
}
